/**
 * Tour engine — a thin wrapper over driver.js.
 *
 * Responsibilities:
 *   - Map our declarative `TourDefinition` to driver.js steps.
 *   - Render the ASU-branded card (title, body, optional embedded screenshot,
 *     "X of N" progress, Skip + Next, top-right close). Styling lives in
 *     `../theme/asu-tour.css`.
 *   - Run each step's async `onBeforeShow` hook and wait for its target to exist
 *     before advancing (so steps can open a drawer/panel first).
 *   - Report completion vs. skip so the manager can persist "seen" state.
 *
 * driver.js gives us the hard parts for free: spotlight overlay, floating-ui
 * positioning + arrow, scroll-into-view, focus handling and keyboard control.
 */

import { driver } from 'driver.js';
import type { Side, Alignment } from 'driver.js';
import type { TourDefinition, TourStep } from './types';

export interface EngineCallbacks {
  /** Fired when the user finishes the last step. */
  onComplete?: () => void;
  /** Fired when the user skips/closes before finishing. */
  onSkip?: () => void;
}

export interface TourEngine {
  start(): Promise<void>;
  destroy(): void;
  /** Tear down WITHOUT firing complete/skip (so "seen" is not recorded). Used to
   *  temporarily hide the tour while a modal is open; it is re-shown afterwards. */
  suspend(): void;
}

/**
 * How long to wait for a step's target element to appear before giving up. Generous so
 * that `?tour=` force-launch (fired on app load) still anchors once the dashboard and its
 * data finish rendering. Resolves immediately when the target is already present.
 */
const TARGET_WAIT_MS = 8000;
const TARGET_POLL_MS = 60;

/** How long to wait for a step's image to decode before showing its card. */
const IMAGE_WAIT_MS = 4000;

/** Natural aspect ratio (width / height) of each preloaded step image, keyed by its src. */
const imageAspect = new Map<string, number>();

/**
 * Load a step's image (and remember its aspect ratio) BEFORE the card is shown. driver.js
 * measures the card once and anchors a `bottom`-placed one by its BOTTOM edge, so an image
 * that decodes later grows the card UPWARD and strands the arrow above the target.
 * Resolves on error/timeout too — a missing image must never block a tour.
 */
function preloadStepImage(step?: TourStep): Promise<void> {
  const src = step?.image;
  if (!src || imageAspect.has(src)) return Promise.resolve();
  return new Promise((resolve) => {
    const img = new Image();
    const timer = window.setTimeout(resolve, IMAGE_WAIT_MS);
    const settle = (): void => {
      window.clearTimeout(timer);
      if (img.naturalWidth && img.naturalHeight) imageAspect.set(src, img.naturalWidth / img.naturalHeight);
      resolve();
    };
    img.onload = settle;
    img.onerror = settle;
    img.src = src;
  });
}

/**
 * Stamp each card image with its known aspect ratio so its box is reserved on the first
 * layout pass — a decoded image isn't guaranteed to lay out in the frame the card is measured.
 */
function reserveImageBoxes(wrapper: HTMLElement): void {
  wrapper.querySelectorAll('img.asu-tour__img').forEach((node) => {
    const img = node as HTMLImageElement;
    // getAttribute (not .src) so the key matches the raw src the step declared.
    const ratio = imageAspect.get(img.getAttribute('src') || '');
    if (ratio) img.style.aspectRatio = String(ratio);
  });
}

/**
 * The design's exact close icon (FontAwesome xmark-solid, 24.4px, ASU Gray #747474 via
 * currentColor). Replaces driver.js's plain-text "×" so the glyph matches 1:1.
 */
const CLOSE_ICON_SVG = `<svg viewBox="0 0 24 24" width="100%" height="100%" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M7.05031 5.35531C6.58156 4.88656 5.82031 4.88656 5.35156 5.35531C4.88281 5.82406 4.88281 6.58531 5.35156 7.05406L10.5041 12.2028L5.35531 17.3553C4.88656 17.8241 4.88656 18.5853 5.35531 19.0541C5.82406 19.5228 6.58531 19.5228 7.05406 19.0541L12.2028 13.9016L17.3553 19.0503C17.8241 19.5191 18.5853 19.5191 19.0541 19.0503C19.5228 18.5816 19.5228 17.8203 19.0541 17.3516L13.9016 12.2028L19.0503 7.05031C19.5191 6.58156 19.5191 5.82031 19.0503 5.35156C18.5816 4.88281 17.8203 4.88281 17.3516 5.35156L12.2028 10.5041L7.05031 5.35531Z" fill="currentColor"/></svg>`;

/**
 * Resolve a selector to the first VISIBLE match. A component can be rendered more than
 * once (e.g. desktop + hidden mobile copies share a `data-tour` hook); a plain
 * querySelector would grab a hidden 0×0 copy and anchor the card to the top-left corner.
 * Falls back to the first match, then undefined.
 */
function findVisibleTarget(selector?: string): Element | undefined {
  if (!selector) return undefined;
  const nodes = Array.from(document.querySelectorAll(selector));
  const visible = nodes.find((el) => {
    const rect = el.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  });
  return visible || nodes[0] || undefined;
}

function waitForTarget(selector?: string): Promise<void> {
  if (!selector || findVisibleTarget(selector)) return Promise.resolve();
  return new Promise((resolve) => {
    const startedAt = Date.now();
    const timer = window.setInterval(() => {
      if (findVisibleTarget(selector) || Date.now() - startedAt > TARGET_WAIT_MS) {
        window.clearInterval(timer);
        resolve();
      }
    }, TARGET_POLL_MS);
  });
}

/**
 * Scroll a step's target to the top of the viewport (with a small gap) when it sets
 * `scrollTo: 'top'`. Because the target is then fully in view, driver.js skips its own
 * (centering) scroll, so the card anchors with room and doesn't get flipped/stranded.
 */
function scrollStepIntoView(step: TourStep): Promise<void> {
  if (step.scrollTo !== 'top' || !step.target) return Promise.resolve();
  const el = findVisibleTarget(step.target);
  if (!el) return Promise.resolve();
  const top = el.getBoundingClientRect().top + window.scrollY - 24;
  window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
  return new Promise((resolve) => window.setTimeout(resolve, 450));
}

function mapPlacement(placement?: string): { side: Side; align: Alignment } {
  const [side, align] = (placement || 'bottom-start').split('-');
  return {
    side: (side || 'bottom') as Side,
    align: (align || 'start') as Alignment
  };
}

function buildDescription(step: TourStep): string {
  const body = `<div class="asu-tour__body">${step.body}</div>`;
  const imgClass = step.imageFit === 'contain' ? 'asu-tour__img asu-tour__img--contain' : 'asu-tour__img';
  const imgStyle = step.imageHeight ? ` style="height:${step.imageHeight}px"` : '';
  const image = step.image
    ? `<img class="${imgClass}"${imgStyle} src="${step.image}" alt="${step.imageAlt || ''}" />`
    : '';
  // Intro/welcome cards show the illustration ABOVE the text; other cards below.
  return step.intro ? image + body : body + image;
}

export function createTourEngine(def: TourDefinition, callbacks: EngineCallbacks = {}): TourEngine {
  let finished = false;

  const complete = (): void => {
    if (finished) return;
    finished = true;
    callbacks.onComplete?.();
  };
  const skip = (): void => {
    if (finished) return;
    finished = true;
    callbacks.onSkip?.();
  };

  const total = def.steps.length;
  const isMultiStep = total > 1;

  // Progress counters exclude `intro` steps (e.g. the welcome). Precompute each step's
  // "N of M" counter (null = no counter) and whether it shows the Skip pill.
  const countedTotal = def.steps.filter((s) => !s.intro).length;
  let countedSoFar = 0;
  const stepMeta = def.steps.map((step) => {
    if (step.intro) return { counter: null as string | null, showSkip: false };
    countedSoFar += 1;
    return { counter: `${countedSoFar} of ${countedTotal}`, showSkip: true };
  });

  // Single-step callouts lock page scroll so the card can't drift off its target.
  // Multi-step tours leave scroll free — driver scrolls each target into view on Next.
  let prevHtmlOverflow = '';
  let prevBodyOverflow = '';
  let scrollLocked = false;
  const lockScroll = (): void => {
    if (scrollLocked || isMultiStep) return;
    scrollLocked = true;
    prevHtmlOverflow = document.documentElement.style.overflow;
    prevBodyOverflow = document.body.style.overflow;
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
  };
  const unlockScroll = (): void => {
    if (!scrollLocked) return;
    scrollLocked = false;
    document.documentElement.style.overflow = prevHtmlOverflow;
    document.body.style.overflow = prevBodyOverflow;
  };

  // Backstop for anything that resizes the card after driver.js positioned it (late image,
  // font swap, re-wrapped copy) — driver otherwise only re-anchors on scroll/resize.
  // refresh() re-runs positioning only, so it can't loop back through this observer.
  let cardObserver: ResizeObserver | null = null;
  let lastCardHeight = 0;
  const watchCardResize = (): void => {
    if (cardObserver || typeof ResizeObserver === 'undefined') return;
    const card = document.querySelector('.driver-popover');
    if (!card) return;
    lastCardHeight = card.getBoundingClientRect().height;
    cardObserver = new ResizeObserver(() => {
      const height = card.getBoundingClientRect().height;
      if (Math.abs(height - lastCardHeight) < 1) return;
      lastCardHeight = height;
      driverObj.refresh();
    });
    cardObserver.observe(card);
  };
  const unwatchCardResize = (): void => {
    cardObserver?.disconnect();
    cardObserver = null;
  };

  const steps = def.steps.map((step) => {
    const { side, align } = mapPlacement(step.placement);
    return {
      // Resolved at highlight time to the visible match (see findVisibleTarget).
      element: step.target ? (): Element => findVisibleTarget(step.target) as Element : undefined,
      popover: {
        title: step.title,
        description: buildDescription(step),
        side,
        align,
        // Per-step advance label (e.g. "Take the tour" on the welcome step).
        ...(step.nextLabel ? { nextBtnText: step.nextLabel } : {})
      }
    };
  });

  const driverObj = driver({
    steps,
    // Single-step tours (e.g. the ea-app Announcements card) show a minimal card:
    // just the × and a "Done" button, no progress and no Skip. Multi-step tours show
    // the "X of N" progress and a Skip pill (see onPopoverRender).
    showProgress: isMultiStep,
    progressText: '{{current}} of {{total}}',
    showButtons: ['next', 'close'],
    nextBtnText: 'Next',
    doneBtnText: 'Done',
    allowClose: true,
    overlayColor: '#1a1a1a',
    overlayOpacity: 0.55,
    // Spotlight hugs the target exactly (no larger than the element).
    stagePadding: 0,
    stageRadius: 18,
    popoverClass: isMultiStep ? 'asu-tour' : 'asu-tour asu-tour--single',
    smoothScroll: true,
    animate: true,
    allowKeyboardControl: true,

    // Fires after the target is highlighted and scrolled into view — safe to lock now.
    onHighlighted: () => {
      lockScroll();
      watchCardResize();
    },

    onPopoverRender: (popover: {
      wrapper: HTMLElement;
      footer: HTMLElement;
      footerButtons: HTMLElement;
      closeButton?: HTMLElement;
      progress?: HTMLElement;
    }) => {
      // Swap driver's plain-text "×" for the design's exact FontAwesome xmark.
      if (popover.closeButton) popover.closeButton.innerHTML = CLOSE_ICON_SVG;
      // Runs BEFORE driver measures/positions the card, so the image's box counts.
      reserveImageBoxes(popover.wrapper);
      if (!isMultiStep) return;

      const index = driverObj.getActiveIndex() ?? 0;
      const meta = stepMeta[index];
      if (!meta) return;

      // Centered welcome/intro steps get an extra class for styling.
      // Toggle (not just add) so the class doesn't leak from the welcome step
      // onto later steps — driver reuses the same popover element across steps.
      popover.wrapper.classList.toggle('asu-tour--intro', !!def.steps[index]?.intro);

      // Optional fractional horizontal nudge for wide-section targets. driver
      // positions via left/top and never touches transform, so this survives its
      // reposition-on-scroll. Cleared for steps that don't set it (wrapper is reused).
      const nudge = def.steps[index]?.popoverNudge;
      if (nudge) {
        const w = driverObj.getActiveElement()?.getBoundingClientRect().width ?? 0;
        popover.wrapper.style.transform = `translateX(${Math.round(w * nudge)}px)`;
      } else {
        popover.wrapper.style.transform = '';
      }

      // Progress: show the counted "N of M", or hide it on uncounted (intro) steps.
      if (popover.progress) {
        if (meta.counter) {
          popover.progress.textContent = meta.counter;
          popover.progress.style.removeProperty('display');
        } else {
          popover.progress.style.display = 'none';
        }
      }

      // Skip pill (not on intro steps), between the progress text and the Next button.
      if (meta.showSkip) {
        const skipBtn = document.createElement('button');
        skipBtn.type = 'button';
        skipBtn.className = 'asu-tour__skip';
        skipBtn.textContent = 'Skip';
        skipBtn.addEventListener('click', () => {
          skip();
          driverObj.destroy();
        });
        popover.footer.insertBefore(skipBtn, popover.footerButtons);
      }
    },

    // We take over navigation so we can await each step's onBeforeShow (e.g. open a
    // panel) and wait for the next target to render before moving.
    onNextClick: async () => {
      const index = driverObj.getActiveIndex() ?? 0;
      if (index >= total - 1) {
        complete();
        driverObj.destroy();
        return;
      }
      const nextStep = def.steps[index + 1];
      await def.steps[index]?.onAfterHide?.();
      await nextStep.onBeforeShow?.();
      await preloadStepImage(nextStep);
      await waitForTarget(nextStep.target);
      await scrollStepIntoView(nextStep);
      driverObj.moveNext();
    },

    onCloseClick: () => {
      skip();
      driverObj.destroy();
    },

    onDestroyed: () => {
      unlockScroll();
      unwatchCardResize();
      // Covers Esc / overlay click paths that don't route through our handlers.
      if (!finished) skip();
    }
  });

  async function start(): Promise<void> {
    const first = def.steps[0];
    if (!first) return;
    await first.onBeforeShow?.();
    await preloadStepImage(first);
    await waitForTarget(first.target);
    await scrollStepIntoView(first);
    driverObj.drive(0);
  }

  function destroy(): void {
    driverObj.destroy();
  }

  function suspend(): void {
    // Mark finished so onDestroyed does not treat this as a skip, then tear down.
    finished = true;
    driverObj.destroy();
  }

  return { start, destroy, suspend };
}
